import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../Dashboard/NotificationBell';
import './MainLayout.css';
import UserSidebar from './UserSidebar';
import ViewAsUserBanner from '../Staff/ViewAsUserBanner';
import FeedbackWidget from '../Feedback/FeedbackWidget';

/**
 * Componente de layout principal com sidebar responsiva
 * Adapta-se ao nível de acesso do usuário e fornece navegação contextual
 */
const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Função para fazer logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Função para alternar sidebar no mobile
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Função para fechar sidebar ao navegar (mobile)
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-black">
      <ViewAsUserBanner />
      {/* Header Mobile */}
      <div className="lg:hidden bg-black/20 backdrop-blur-sm border-b border-white/10 p-4 sticky top-0 z-[60]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="text-white hover:bg-white/10"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold text-white">
              Central de Atendimento
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <NotificationBell iconClassName="text-white h-5 w-5" />
            <span className="text-sm text-gray-300">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar Desktop */}
        <div className="hidden lg:block w-64 h-screen fixed left-0 top-0 z-[100] shadow-2xl">
          <UserSidebar />
        </div>

        {/* Sidebar Mobile */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="lg:hidden fixed inset-0 bg-black/80 z-[110] backdrop-blur-sm"
                onClick={closeSidebar}
              />

              {/* Sidebar Drawer */}
              <motion.div
                initial={{ x: -300 }}
                animate={{ x: 0 }}
                exit={{ x: -300 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="lg:hidden fixed left-0 top-0 w-72 h-full z-[120] shadow-2xl"
              >
                <div className="h-full relative">
                  <UserSidebar onClose={closeSidebar} />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeSidebar}
                    className="absolute top-4 right-4 text-white hover:bg-white/10 lg:hidden"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Conteúdo Principal */}
        <div className="flex-1 min-h-screen lg:ml-64 w-full">
          <main className="p-4 lg:p-8 pt-6 w-full max-w-[1600px] mx-auto">
            <Outlet />
          </main>
        </div>
      </div>
      <FeedbackWidget />
    </div>
  );
};

export default MainLayout;
